import React, { useEffect, useMemo, useRef, useState } from "react";
import { sendShopStaffMsc, getShopId } from "../utils/api";

const MAX_RECIPIENTS = 50;

function newRequestId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

let rowSeq = 0;
function makeRow(username = "", amount = "") {
  rowSeq += 1;
  return { key: `r${Date.now()}-${rowSeq}`, username, amount: amount === "" ? "" : String(amount) };
}

function cleanUsername(u) {
  return String(u || "").trim().replace(/^@+/, "");
}

function parseAmount(a) {
  const n = Number(String(a ?? "").trim());
  return Number.isFinite(n) ? n : NaN;
}

function hasAtMost2Decimals(n) {
  return Math.round(n * 100) / 100 === n;
}

// Saved staff list lives in this browser only, per shop. Storage can throw
// (private mode, blocked site data), so every access is guarded.
function storageKey() {
  let id = "";
  try { id = getShopId() || ""; } catch { id = ""; }
  return `moondala_shop_staff_list:${id || "default"}`;
}

function readSavedStaff() {
  try {
    const raw = window.localStorage.getItem(storageKey());
    const arr = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(arr)) return [];
    return arr
      .map((x) => ({ username: cleanUsername(x?.username), amountMsc: Number(x?.amountMsc) || 0 }))
      .filter((x) => x.username)
      .slice(0, MAX_RECIPIENTS);
  } catch {
    return [];
  }
}

function writeSavedStaff(list) {
  try {
    window.localStorage.setItem(storageKey(), JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

// A thrown request error is a definite server rejection when the server answered
// with a 4xx (other than timeout / rate limit). Anything else (no response, 5xx,
// 408, 429) may or may not have been processed, so the same requestId is reused.
function isDefiniteRejection(err) {
  const st = Number(err?.status || 0);
  return st >= 400 && st < 500 && st !== 408 && st !== 429;
}

export default function ShopStaffCredit({ balanceMsc = null, onBalanceChange }) {
  const [rows, setRows] = useState(() => [makeRow()]);
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState(() => readSavedStaff());
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [results, setResults] = useState(null);
  // Set after an ambiguous failure: the form is frozen so a retry re-sends the
  // exact same payload under the same requestId.
  const [retryPending, setRetryPending] = useState(false);
  const requestIdRef = useRef(null);
  const pendingPayloadRef = useRef(null);

  useEffect(() => {
    if (!info) return undefined;
    const t = setTimeout(() => setInfo(""), 3000);
    return () => clearTimeout(t);
  }, [info]);

  const total = useMemo(() => {
    const cents = rows.reduce((sum, r) => {
      const n = parseAmount(r.amount);
      return Number.isFinite(n) && n > 0 ? sum + Math.round(n * 100) : sum;
    }, 0);
    return cents / 100;
  }, [rows]);

  const locked = sending || retryPending;
  const knownBalance = typeof balanceMsc === "number" && Number.isFinite(balanceMsc) ? balanceMsc : null;

  function updateRow(key, field, value) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
    setError("");
  }

  function addRow(username = "", amount = "") {
    if (rows.length >= MAX_RECIPIENTS) {
      setError(`You can send to at most ${MAX_RECIPIENTS} people at once.`);
      return;
    }
    setRows((prev) => {
      if (prev.length >= MAX_RECIPIENTS) return prev;
      // Fill the single empty starter row instead of appending next to it.
      if (username && prev.length === 1 && !prev[0].username && !prev[0].amount) {
        return [makeRow(username, amount)];
      }
      return [...prev, makeRow(username, amount)];
    });
  }

  function removeRow(key) {
    setRows((prev) => {
      const next = prev.filter((r) => r.key !== key);
      return next.length ? next : [makeRow()];
    });
    setError("");
  }

  function loadSavedList() {
    if (!saved.length) return;
    setRows(saved.slice(0, MAX_RECIPIENTS).map((s) => makeRow(s.username, s.amountMsc > 0 ? s.amountMsc : "")));
    setResults(null);
    setError("");
  }

  function saveCurrentList() {
    const list = [];
    const seen = new Set();
    for (const r of rows) {
      const u = cleanUsername(r.username);
      if (!u || seen.has(u.toLowerCase())) continue;
      seen.add(u.toLowerCase());
      const n = parseAmount(r.amount);
      list.push({ username: u, amountMsc: Number.isFinite(n) && n > 0 ? n : 0 });
    }
    if (!list.length) {
      setError("Add at least one username before saving the list.");
      return;
    }
    if (writeSavedStaff(list)) {
      setSaved(list);
      setInfo(`Saved ${list.length} staff member${list.length === 1 ? "" : "s"}.`);
    } else {
      setError("Couldn't save the list in this browser.");
    }
  }

  function removeSaved(username) {
    const next = saved.filter((s) => s.username.toLowerCase() !== username.toLowerCase());
    writeSavedStaff(next);
    setSaved(next);
  }

  function buildPayload() {
    const recipients = [];
    const seen = new Set();
    for (const r of rows) {
      const username = cleanUsername(r.username);
      const amtRaw = String(r.amount ?? "").trim();
      if (!username && !amtRaw) continue; // ignore fully blank rows
      if (!username) return { err: "Every row needs a username." };
      if (/\s/.test(username)) return { err: `"${username}" isn't a valid username.` };
      const key = username.toLowerCase();
      if (seen.has(key)) return { err: `@${username} is listed twice. Combine them into one row.` };
      seen.add(key);
      const amountMsc = parseAmount(amtRaw);
      if (!Number.isFinite(amountMsc) || amountMsc <= 0) return { err: `Enter an amount for @${username}.` };
      if (!hasAtMost2Decimals(amountMsc)) return { err: `Use at most 2 decimals for @${username}.` };
      recipients.push({ username, amountMsc });
    }
    if (!recipients.length) return { err: "Add at least one staff member and amount." };
    if (recipients.length > MAX_RECIPIENTS) return { err: `You can send to at most ${MAX_RECIPIENTS} people at once.` };
    const totalCents = recipients.reduce((s, r) => s + Math.round(r.amountMsc * 100), 0);
    if (knownBalance !== null && totalCents > Math.round(knownBalance * 100)) {
      return { err: `Total ${(totalCents / 100).toFixed(2)} MSC is more than your balance of ${knownBalance.toFixed(2)} MSC.` };
    }
    return { payload: { recipients, note: note.trim() } };
  }

  async function doSend(payload) {
    if (!requestIdRef.current) requestIdRef.current = newRequestId();
    const requestId = requestIdRef.current;
    pendingPayloadRef.current = payload;
    setSending(true);
    setError("");
    try {
      const res = await sendShopStaffMsc({ ...payload, requestId });
      if (res && res.ok) {
        // Settled: the next send is a new transfer batch.
        requestIdRef.current = null;
        pendingPayloadRef.current = null;
        setRetryPending(false);
        const list = Array.isArray(res.results) ? res.results : [];
        setResults({ items: list, note: payload.note });
        if (res.newBalanceMsc !== undefined && res.newBalanceMsc !== null && typeof onBalanceChange === "function") {
          const nb = Number(res.newBalanceMsc);
          if (Number.isFinite(nb)) onBalanceChange(nb);
        }
        // Keep only the recipients that failed, so they can be fixed and re-sent.
        const failed = list.filter((r) => !r.ok).map((r) => cleanUsername(r?.username).toLowerCase());
        if (failed.length) {
          const failedRows = payload.recipients
            .filter((r) => failed.includes(r.username.toLowerCase()))
            .map((r) => makeRow(r.username, r.amountMsc));
          setRows(failedRows.length ? failedRows : [makeRow()]);
        } else {
          setRows([makeRow()]);
          setNote("");
        }
      } else {
        // Definite rejection: nothing was sent; a retry is a new request.
        requestIdRef.current = null;
        pendingPayloadRef.current = null;
        setRetryPending(false);
        setError(friendlyError(res?.code, res?.error || res?.message));
      }
    } catch (err) {
      if (isDefiniteRejection(err)) {
        requestIdRef.current = null;
        pendingPayloadRef.current = null;
        setRetryPending(false);
        setError(friendlyError(err?.data?.code, err?.data?.error || err?.message));
      } else {
        // Unknown outcome: keep requestId + payload so a retry can't pay twice.
        setRetryPending(true);
        setError("Connection problem. The send may or may not have gone through. Retry is safe: it won't pay anyone twice.");
      }
    } finally {
      setSending(false);
    }
  }

  function handleSend() {
    if (sending) return;
    if (retryPending && pendingPayloadRef.current) {
      doSend(pendingPayloadRef.current);
      return;
    }
    const { payload, err } = buildPayload();
    if (err) {
      setError(err);
      return;
    }
    setResults(null);
    doSend(payload);
  }

  function discardPending() {
    // The user chooses to abandon an ambiguous send. Unlock the form, and warn
    // them to check balance/history before re-sending under a new request.
    requestIdRef.current = null;
    pendingPayloadRef.current = null;
    setRetryPending(false);
    setError("Unlocked. Check your MSC balance before sending again, the previous send may have completed.");
  }

  const nonEmptyCount = rows.filter((r) => cleanUsername(r.username) || String(r.amount).trim()).length;

  return (
    <div style={S.card}>
      <div style={S.headRow}>
        <div>
          <div style={S.h2}>👥 Send MSC to staff</div>
          <div style={S.muted}>Pay bonuses or perks to your team from your shop's MSC balance.</div>
        </div>
        {knownBalance !== null && (
          <div style={S.balancePill}>Balance: {knownBalance.toFixed(2)} MSC</div>
        )}
      </div>

      <div style={S.notice}>
        Your staff can spend MSC at any Moondala shop. MSC sent to staff can't be cashed out by them.
      </div>

      {/* Saved staff list */}
      <div style={S.savedBox}>
        <div style={S.savedHead}>
          <div style={S.label}>Saved staff list{saved.length ? ` (${saved.length})` : ""}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" style={S.btnGhost} onClick={loadSavedList} disabled={locked || !saved.length}>
              Load list
            </button>
            <button type="button" style={S.btnGhost} onClick={saveCurrentList} disabled={locked}>
              Save current as list
            </button>
          </div>
        </div>
        {saved.length ? (
          <div style={S.chips}>
            {saved.map((s) => (
              <span key={s.username} style={S.chip}>
                <button
                  type="button"
                  style={S.chipAdd}
                  disabled={locked}
                  title={`Add @${s.username}`}
                  onClick={() => {
                    const exists = rows.some((r) => cleanUsername(r.username).toLowerCase() === s.username.toLowerCase());
                    if (!exists) addRow(s.username, s.amountMsc > 0 ? s.amountMsc : "");
                  }}
                >
                  + @{s.username}{s.amountMsc > 0 ? ` · ${s.amountMsc}` : ""}
                </button>
                <button
                  type="button"
                  style={S.chipX}
                  aria-label={`Remove @${s.username} from saved list`}
                  onClick={() => removeSaved(s.username)}
                  disabled={locked}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : (
          <div style={S.mutedSm}>No saved list yet. Add your staff below, then tap "Save current as list".</div>
        )}
      </div>

      {/* Recipient rows */}
      <div style={S.rows}>
        {rows.map((r, i) => (
          <div key={r.key} style={S.row}>
            <div style={S.atWrap}>
              <span style={S.at}>@</span>
              <input
                type="text"
                value={r.username}
                onChange={(e) => updateRow(r.key, "username", e.target.value)}
                placeholder="username"
                aria-label={`Recipient ${i + 1} username`}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                disabled={locked}
                style={{ ...S.input, paddingLeft: 26 }}
              />
            </div>
            <input
              type="number"
              inputMode="decimal"
              min="0.01"
              step="0.01"
              value={r.amount}
              onChange={(e) => updateRow(r.key, "amount", e.target.value)}
              placeholder="MSC"
              aria-label={`Recipient ${i + 1} amount in MSC`}
              disabled={locked}
              style={{ ...S.input, ...S.amount }}
            />
            <button
              type="button"
              style={S.btnRemove}
              onClick={() => removeRow(r.key)}
              disabled={locked}
              aria-label={`Remove recipient ${i + 1}`}
              title="Remove"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <div style={S.rowFooter}>
        <button
          type="button"
          style={S.btnGhost}
          onClick={() => addRow()}
          disabled={locked || rows.length >= MAX_RECIPIENTS}
        >
          + Add staff
        </button>
        <span style={S.mutedSm}>{rows.length}/{MAX_RECIPIENTS}</span>
      </div>

      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note (optional), e.g. October bonus"
        maxLength={140}
        disabled={locked}
        style={{ ...S.input, marginTop: 12 }}
      />

      <div style={S.totalRow}>
        <div>
          <div style={S.label}>Total</div>
          <div style={S.total}>{total.toFixed(2)} MSC</div>
          <div style={S.mutedSm}>
            {nonEmptyCount} recipient{nonEmptyCount === 1 ? "" : "s"}
            {knownBalance !== null && total > knownBalance ? " · exceeds balance" : ""}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
          {retryPending && !sending && (
            <button type="button" style={S.btnGhost} onClick={discardPending}>
              Start over
            </button>
          )}
          <button
            type="button"
            style={{ ...S.btnSend, opacity: sending ? 0.7 : 1 }}
            onClick={handleSend}
            disabled={sending}
          >
            {sending ? "Sending..." : retryPending ? "Retry send" : `Send ${total.toFixed(2)} MSC`}
          </button>
        </div>
      </div>

      {error && <div style={S.err} role="alert">{error}</div>}
      {info && <div style={S.ok} role="status">{info}</div>}

      {results && (
        <div style={S.results}>
          <div style={S.label}>
            Results · {results.items.filter((x) => x.ok).length} sent
            {results.items.some((x) => !x.ok) ? ` · ${results.items.filter((x) => !x.ok).length} failed` : ""}
          </div>
          {results.items.length === 0 && <div style={S.mutedSm}>No per-recipient results returned.</div>}
          {results.items.map((x, i) => (
            <div key={`${x.username}-${i}`} style={S.resultRow}>
              <span style={{ color: x.ok ? "var(--success, #22c55e)" : "var(--danger, #ef4444)", fontWeight: 900 }}>
                {x.ok ? "✓" : "✗"}
              </span>
              <span style={{ fontWeight: 700 }}>@{x.username}</span>
              <span style={S.mutedSm}>
                {x.ok
                  ? x.status === "pending_onchain"
                    ? "Sent · recording on-chain"
                    : "Sent"
                  : x.error || "Failed"}
              </span>
            </div>
          ))}
          {results.items.some((x) => !x.ok) && (
            <div style={S.mutedSm}>Failed recipients are kept in the list above. Fix them and send again.</div>
          )}
        </div>
      )}
    </div>
  );
}

function friendlyError(code, message) {
  const c = String(code || "").toUpperCase();
  if (c === "INSUFFICIENT_BALANCE" || c === "INSUFFICIENT_FUNDS") return "Not enough MSC in your shop balance.";
  if (c === "TOO_MANY_RECIPIENTS") return `You can send to at most ${MAX_RECIPIENTS} people at once.`;
  return message || "Send failed. Nothing was sent.";
}

const S = {
  card: {
    marginTop: 24,
    background: "var(--card-bg)",
    borderRadius: 16,
    padding: 20,
    border: "1.5px solid rgba(139,92,246,0.5)",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    color: "var(--text-primary)",
  },
  headRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" },
  h2: { fontSize: 20, fontWeight: 800, color: "var(--text-primary)", marginBottom: 4 },
  muted: { fontSize: 13, color: "var(--text-secondary)", fontWeight: 500 },
  mutedSm: { fontSize: 12, color: "var(--text-secondary)", fontWeight: 500 },
  label: {
    fontSize: 12,
    fontWeight: 700,
    color: "var(--text-secondary)",
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    marginBottom: 6,
  },
  balancePill: {
    padding: "6px 12px",
    borderRadius: 999,
    background: "rgba(139,92,246,0.12)",
    color: "#a78bfa",
    fontWeight: 800,
    fontSize: 13,
    whiteSpace: "nowrap",
  },
  notice: {
    marginTop: 12,
    padding: "10px 14px",
    background: "rgba(139,92,246,0.08)",
    border: "1px solid rgba(139,92,246,0.3)",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text-secondary)",
  },
  savedBox: {
    marginTop: 16,
    padding: 12,
    borderRadius: 12,
    border: "1px dashed var(--border-color)",
  },
  savedHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 },
  chips: { display: "flex", flexWrap: "wrap", gap: 6 },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    border: "1px solid var(--border-color)",
    background: "var(--bg-secondary)",
    overflow: "hidden",
  },
  chipAdd: {
    background: "transparent",
    border: "none",
    color: "var(--text-primary)",
    padding: "6px 4px 6px 10px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  chipX: {
    background: "transparent",
    border: "none",
    color: "var(--text-secondary)",
    padding: "6px 10px 6px 6px",
    fontSize: 14,
    cursor: "pointer",
    lineHeight: 1,
  },
  rows: { marginTop: 16, display: "flex", flexDirection: "column", gap: 8 },
  row: { display: "flex", gap: 8, alignItems: "center" },
  atWrap: { position: "relative", flex: 1, minWidth: 0 },
  at: {
    position: "absolute",
    left: 10,
    top: "50%",
    transform: "translateY(-50%)",
    color: "var(--text-secondary)",
    fontWeight: 700,
    fontSize: 14,
    pointerEvents: "none",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid var(--border-color)",
    background: "var(--bg-secondary)",
    color: "var(--text-primary)",
    fontSize: 14,
    outline: "none",
  },
  amount: { width: 110, flex: "0 0 110px" },
  btnRemove: {
    flex: "0 0 36px",
    height: 38,
    borderRadius: 10,
    border: "1px solid var(--border-color)",
    background: "transparent",
    color: "var(--text-secondary)",
    fontSize: 18,
    cursor: "pointer",
    lineHeight: 1,
  },
  rowFooter: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 },
  btnGhost: {
    background: "var(--btn-secondary-bg)",
    border: "1.5px solid var(--btn-outline-border)",
    color: "var(--btn-secondary-text)",
    padding: "8px 14px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 13,
  },
  totalRow: {
    marginTop: 16,
    paddingTop: 16,
    borderTop: "1px solid var(--border-color)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 12,
    flexWrap: "wrap",
  },
  total: { fontSize: 24, fontWeight: 900, color: "var(--text-primary)", lineHeight: 1.1, marginBottom: 2 },
  btnSend: {
    background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
    border: "none",
    color: "#fff",
    padding: "12px 20px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 14,
  },
  err: {
    marginTop: 12,
    padding: 12,
    background: "rgba(248, 113, 113, 0.12)",
    borderRadius: 10,
    color: "var(--danger)",
    border: "1.5px solid rgba(248, 113, 113, 0.4)",
    fontWeight: 600,
    fontSize: 13,
  },
  ok: {
    marginTop: 12,
    padding: 12,
    background: "rgba(34, 197, 94, 0.12)",
    borderRadius: 10,
    color: "var(--success)",
    border: "1.5px solid rgba(34, 197, 94, 0.4)",
    fontWeight: 600,
    fontSize: 13,
  },
  results: {
    marginTop: 16,
    padding: 12,
    borderRadius: 12,
    background: "var(--bg-tertiary)",
    border: "1px solid var(--border-color)",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  resultRow: { display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap", fontSize: 14 },
};
